// Pantalla principal del local: el encargado carga el pedido desde el celular.
// Pensada para el pulgar: categorias colapsables, botones - / + grandes y el
// total siempre a la vista abajo.
//
// El pedido se manda en PENDIENTE: no entra a produccion ni a la cuenta
// corriente hasta que administracion lo confirma.

import { api } from "../api";
import { navegar } from "../router";
import { iconoChevron, iconoCategoria, iconoTilde } from "../iconos";
import { montarBuscador, marcarFila } from "../buscador";
import { formatearPesos } from "../../lib/money";
import { calcularSubtotal, calcularTotalPedido } from "../../lib/calculo";
import { hoyMontevideo, esFechaPasada } from "../../lib/fecha";
import { TURNOS } from "../../lib/types";
import type {
  PreparaPedido,
  PreparaItem,
  LineaDeLocal,
  LocalDeUsuario,
  PedidoDeLocal,
  Sesion,
  Turno,
} from "../../lib/types";

// El borrador se guarda POR LOCAL: si una misma persona maneja Tres Cruces y
// Montevideo Shopping, lo que carga en uno no puede aparecerle en el otro.
const borradorDe = (clienteId: number) => `cliente_pedido_borrador_${clienteId}`;
const EDITANDO = "cliente_pedido_editando";
const LOCAL_ELEGIDO = "cliente_local_elegido";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

/** Deja cargado un pedido pendiente para corregirlo desde esta pantalla. */
export function editarPedidoPendiente(id: number, clienteId: number, lineas: LineaDeLocal[]): void {
  sessionStorage.setItem(EDITANDO, String(id));
  sessionStorage.setItem(LOCAL_ELEGIDO, String(clienteId));
  localStorage.setItem(
    borradorDe(clienteId),
    JSON.stringify(lineas.map((l) => [l.producto_id, l.cantidad])),
  );
}

export async function renderClientePedido(contenedor: HTMLElement): Promise<void> {
  const editandoId = Number(sessionStorage.getItem(EDITANDO)) || null;

  contenedor.innerHTML = `<div class="tarjeta text-center text-masa-400">Cargando el catálogo...</div>`;

  // Los locales que maneja esta persona. Si es uno solo, ni se muestra el
  // selector; si son varios, elige para cual es el pedido.
  let locales: LocalDeUsuario[] = [];
  try {
    locales = (await api.get<Sesion>("/api/sesion")).locales ?? [];
  } catch {
    /* sin sesion no deberia llegar aca */
  }
  const guardadoLocal = Number(sessionStorage.getItem(LOCAL_ELEGIDO)) || null;
  let clienteId =
    guardadoLocal && locales.some((l) => l.id === guardadoLocal)
      ? guardadoLocal
      : (locales[0]?.id ?? 0);

  let prepara: PreparaPedido;
  try {
    prepara = await api.get<PreparaPedido>(`/api/cliente/preparar?cliente_id=${clienteId}`);
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-red-700">No se pudo cargar el catálogo: ${esc(
      e instanceof Error ? e.message : "error",
    )}</div>`;
    return;
  }

  // Solo lo que el local puede pedir: precio 0 = no disponible para el.
  const disponibles = prepara.items.filter((i) => i.precio > 0);

  const cantidades = new Map<number, number>();
  try {
    const guardado: unknown = JSON.parse(localStorage.getItem(borradorDe(clienteId)) ?? "[]");
    if (Array.isArray(guardado)) {
      for (const par of guardado) {
        if (Array.isArray(par) && typeof par[0] === "number" && typeof par[1] === "number") {
          cantidades.set(par[0], par[1]);
        }
      }
    }
  } catch {
    /* borrador corrupto -> se arranca de cero */
  }

  const selectorLocal =
    locales.length > 1
      ? `<label class="tarjeta mb-3 flex items-center justify-between gap-3 py-3">
          <span class="etiqueta mb-0">Local</span>
          <select id="local" class="campo max-w-[14rem]">
            ${locales
              .map(
                (l) =>
                  `<option value="${l.id}"${l.id === clienteId ? " selected" : ""}>${esc(l.nombre)}</option>`,
              )
              .join("")}
          </select>
        </label>`
      : "";

  // Nunca para atras: el calendario del celular arranca en hoy. Es solo la
  // primera barrera (comoda); la que manda es la del servidor.
  const hoy = hoyMontevideo();
  const fechaInicial = esFechaPasada(prepara.fecha_sugerida) ? hoy : prepara.fecha_sugerida;

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">${editandoId ? "Corregir el pedido" : "Hacer un pedido"}</h1>
      <p class="subtitulo">${esc(prepara.cliente.nombre)}</p>
    </div>

    <div id="contenido">
      ${selectorLocal}
      <label class="tarjeta mb-3 flex items-center justify-between gap-3 py-3">
        <span class="etiqueta mb-0">Fecha de entrega</span>
        <input id="fecha" type="date" class="campo max-w-[11rem]"
               min="${esc(hoy)}" value="${esc(fechaInicial)}" />
      </label>

      <div class="tarjeta mb-3 flex items-center justify-between gap-3 py-3">
        <span class="etiqueta mb-0">Turno</span>
        <div id="turnos" class="flex gap-2">
          ${TURNOS.map(
            (t, i) =>
              `<button type="button" data-turno="${t.valor}"
                class="btn-sec turno-btn${i === 0 ? " turno-activo" : ""}">${t.nombre}</button>`,
          ).join("")}
        </div>
      </div>

      <div id="buscador" class="mb-3"></div>

      <div id="grilla" class="space-y-3"></div>
    </div>

    <div id="barra" class="fixed inset-x-0 bottom-0 z-20 border-t border-crema-200 bg-papel/95 px-4 py-3 backdrop-blur">
      <div class="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2">
        <div>
          <div class="text-xs uppercase tracking-wider text-masa-400">Total estimado</div>
          <div id="total" class="text-xl font-bold tabular-nums">$ 0</div>
        </div>
        <div class="flex items-center gap-2">
          <button id="limpiar" class="btn-sec">Limpiar</button>
          <button id="enviar" class="btn-primario">${editandoId ? "Guardar cambios" : "Enviar pedido"}</button>
        </div>
      </div>
      <p id="err" class="mx-auto mt-1 max-w-5xl text-sm text-red-600"></p>
    </div>

    <dialog id="dlg"></dialog>`;

  const grilla = contenedor.querySelector("#grilla") as HTMLElement;
  const barra = contenedor.querySelector("#barra") as HTMLElement;
  const contenido = contenedor.querySelector("#contenido") as HTMLElement;
  const elTotal = contenedor.querySelector("#total") as HTMLElement;
  const err = contenedor.querySelector("#err") as HTMLElement;
  const inpFecha = contenedor.querySelector("#fecha") as HTMLInputElement;
  const selLocal = contenedor.querySelector("#local") as HTMLSelectElement | null;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;

  // Buscador: se escribe el nombre, se elige y la pantalla abre la categoria y
  // deja la fila marcada con el cursor en la cantidad.
  const nombreCat = new Map(prepara.categorias.map((c) => [c.id, c.nombre]));
  montarBuscador(contenedor.querySelector("#buscador") as HTMLElement, {
    placeholder: "Buscar un artículo...",
    items: () =>
      disponibles.map((it) => {
        const cant = cantidades.get(it.producto.id) ?? 0;
        return {
          id: it.producto.id,
          descripcion: it.producto.descripcion,
          codigo: String(it.producto.codigo),
          categoria_id: it.producto.categoria_id,
          categoria: nombreCat.get(it.producto.categoria_id) ?? "",
          detalle: cant > 0 ? `${cant} cargados` : formatearPesos(it.precio),
        };
      }),
    alElegir: (item) => marcarFila(grilla, item.id, item.categoria_id),
  });

  // Turno: obligatorio, arranca en Manana (lo habitual) pero se elige siempre.
  let turno: Turno = "MANANA";
  contenedor.querySelector("#turnos")?.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>("[data-turno]");
    if (!btn) return;
    turno = btn.dataset.turno as Turno;
    contenedor
      .querySelectorAll<HTMLElement>(".turno-btn")
      .forEach((b) => b.classList.toggle("turno-activo", b.dataset.turno === turno));
  });

  // Cambiar de local recarga la pantalla: cambia el catalogo, los precios y el
  // borrador. Se guarda la eleccion para no tener que repetirla.
  selLocal?.addEventListener("change", () => {
    sessionStorage.setItem(LOCAL_ELEGIDO, selLocal.value);
    void renderClientePedido(contenedor);
  });

  // La barra fija se envuelve en varias filas en el celular y tapaba el ultimo
  // articulo: reservamos abajo su alto REAL + aire, nunca un padding fijo.
  function ajustarEspacioBarra(): void {
    contenido.style.paddingBottom = `${barra.offsetHeight + 16}px`;
  }
  if ("ResizeObserver" in window) new ResizeObserver(ajustarEspacioBarra).observe(barra);
  window.addEventListener("resize", ajustarEspacioBarra);

  function guardarBorrador(): void {
    localStorage.setItem(borradorDe(clienteId), JSON.stringify([...cantidades.entries()]));
  }

  function recalcular(): number {
    const lineas = [...cantidades.entries()]
      .filter(([, c]) => c > 0)
      .map(([pid, c]) => {
        const it = disponibles.find((i) => i.producto.id === pid);
        return { subtotal: calcularSubtotal(it?.precio ?? 0, c) };
      });
    const total = calcularTotalPedido(lineas, 0);
    elTotal.textContent = formatearPesos(total);
    guardarBorrador();
    return total;
  }

  function catAbierta(catId: number): boolean {
    return localStorage.getItem(`cliente_cat_abierta_${catId}`) !== null;
  }

  function pintar(): void {
    const porCat = new Map<number, PreparaItem[]>();
    for (const it of disponibles) {
      const arr = porCat.get(it.producto.categoria_id) ?? [];
      arr.push(it);
      porCat.set(it.producto.categoria_id, arr);
    }

    const bloques: string[] = [];
    for (const cat of prepara.categorias) {
      const items = porCat.get(cat.id);
      if (!items || items.length === 0) continue;
      const cargados = items.reduce((n, it) => n + ((cantidades.get(it.producto.id) ?? 0) > 0 ? 1 : 0), 0);

      const filas = items
        .map((it) => {
          const pid = it.producto.id;
          const cant = cantidades.get(pid) ?? 0;
          return `
          <div data-fila="${pid}" class="flex items-center gap-2 border-t border-crema-100 py-2.5">
            <div class="min-w-0 flex-1">
              <div class="font-medium leading-tight">${esc(it.producto.descripcion)}</div>
              <div class="text-xs text-masa-400">${formatearPesos(it.precio)}${
                it.producto.empaque > 1 ? ` · bandeja de ${esc(it.producto.empaque)}` : ""
              }</div>
            </div>
            <div class="flex shrink-0 items-center gap-1">
              <button class="stepper-btn" data-step="-1" data-pid="${pid}" aria-label="Menos">−</button>
              <input class="cant" type="number" min="0" step="any" inputmode="numeric"
                     data-pid="${pid}" value="${cant || ""}" placeholder="0" />
              <button class="stepper-btn" data-step="1" data-pid="${pid}" aria-label="Más">+</button>
            </div>
            <div class="w-20 shrink-0 text-right text-sm font-medium tabular-nums" data-sub="${pid}">${
              cant > 0 ? formatearPesos(calcularSubtotal(it.precio, cant)) : ""
            }</div>
          </div>`;
        })
        .join("");

      bloques.push(`
        <details ${catAbierta(cat.id) ? "open" : ""} data-cat="${cat.id}" class="tarjeta p-0">
          <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3.5 text-sm font-bold uppercase tracking-wide text-masa-700">
            <span class="text-masa-500">${iconoCategoria(cat.nombre)}</span>
            <span class="flex-1">${esc(cat.nombre)}</span>
            <span data-cont="${cat.id}" class="rounded-full bg-acento-600 px-2 py-0.5 text-xs font-bold text-white${cargados > 0 ? "" : " hidden"}">${cargados}</span>
            <span class="chevron text-masa-400">${iconoChevron}</span>
          </summary>
          <div class="px-4 pb-3">${filas}</div>
        </details>`);
    }

    grilla.innerHTML =
      bloques.join("") ||
      `<div class="tarjeta text-center text-masa-400">Todavia no hay artículos con precio para tu local. Avisale a la confitería.</div>`;

    grilla.querySelectorAll<HTMLDetailsElement>("details[data-cat]").forEach((d) => {
      d.addEventListener("toggle", () => {
        const catId = d.dataset.cat as string;
        if (d.open) localStorage.setItem(`cliente_cat_abierta_${catId}`, "1");
        else localStorage.removeItem(`cliente_cat_abierta_${catId}`);
      });
    });

    recalcular();
    ajustarEspacioBarra();
  }

  /**
   * El numerito de la categoria: cuantos articulos distintos lleva cargados.
   *
   * Se recalcula en el momento. Antes solo se armaba al dibujar toda la grilla,
   * asi que al tocar − / + quedaba con el numero viejo hasta salir de la
   * pantalla y volver a entrar.
   */
  function actualizarContador(catId: number): void {
    const cargados = disponibles.reduce(
      (n, i) =>
        n + (i.producto.categoria_id === catId && (cantidades.get(i.producto.id) ?? 0) > 0 ? 1 : 0),
      0,
    );
    const chip = grilla.querySelector(`[data-cont="${catId}"]`);
    if (!chip) return;
    chip.textContent = String(cargados);
    chip.classList.toggle("hidden", cargados === 0);
  }

  function fijarCantidad(pid: number, valor: number): void {
    const cant = Math.max(0, valor);
    if (cant > 0) cantidades.set(pid, cant);
    else cantidades.delete(pid);

    const it = disponibles.find((i) => i.producto.id === pid);
    const celda = grilla.querySelector(`[data-sub="${pid}"]`);
    if (it && celda) {
      celda.textContent = cant > 0 ? formatearPesos(calcularSubtotal(it.precio, cant)) : "";
    }
    const input = grilla.querySelector<HTMLInputElement>(`input.cant[data-pid="${pid}"]`);
    if (input && Number(input.value || 0) !== cant) input.value = cant ? String(cant) : "";
    if (it) actualizarContador(it.producto.categoria_id);
    recalcular();
  }

  grilla.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>("[data-step]");
    if (!btn) return;
    const pid = Number(btn.dataset.pid);
    const paso = Number(btn.dataset.step);
    fijarCantidad(pid, (cantidades.get(pid) ?? 0) + paso);
  });

  grilla.addEventListener("input", (ev) => {
    const input = ev.target as HTMLInputElement;
    if (!input.classList.contains("cant")) return;
    fijarCantidad(Number(input.dataset.pid), Number(input.value || 0));
  });

  contenedor.querySelector("#limpiar")?.addEventListener("click", () => {
    cantidades.clear();
    localStorage.removeItem(borradorDe(clienteId));
    pintar();
  });

  /** Lo que se acaba de mandar, para mostrarlo sin volver a pedirlo al servidor. */
  interface LineaEnviada {
    descripcion: string;
    cantidad: number;
    subtotal: number;
  }

  function pantallaEnviado(fecha: string, total: number, lineas: LineaEnviada[]): void {
    const turnoTexto = TURNOS.find((t) => t.valor === turno)?.nombre ?? "";
    contenedor.innerHTML = `
      <div class="tarjeta mx-auto mt-6 max-w-md text-center">
        <div class="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700">${iconoTilde}</div>
        <h2 class="mb-1 text-xl font-bold">Pedido enviado</h2>
        <p class="mb-1 text-masa-600">${esc(prepara.cliente.nombre)} · ${esc(fecha)} de ${esc(turnoTexto.toLowerCase())} · ${formatearPesos(total)}</p>
        <p class="mb-4 text-sm text-masa-500">
          Queda <b>pendiente</b> hasta que la confitería lo confirme. Mientras tanto lo podés
          corregir o cancelar desde <b>Mis pedidos</b>.
        </p>

        <!-- Tope de alto para que los dos botones queden a la vista aunque el
             pedido sea largo: la lista scrollea adentro. -->
        <div class="mb-5 max-h-[38vh] overflow-y-auto text-left">
          ${lineas
            .map(
              (l) => `<div class="flex items-center justify-between gap-2 border-t border-crema-100 py-2 text-sm">
                <span class="min-w-0 flex-1">${esc(l.descripcion)}</span>
                <span class="shrink-0 text-masa-500">${esc(l.cantidad)} ×</span>
                <span class="w-20 shrink-0 text-right tabular-nums">${formatearPesos(l.subtotal)}</span>
              </div>`,
            )
            .join("")}
          <div class="flex items-center justify-between gap-2 border-t-2 border-crema-200 py-2 text-sm font-bold">
            <span>Total</span>
            <span class="tabular-nums">${formatearPesos(total)}</span>
          </div>
        </div>

        <div class="flex justify-center gap-2">
          <button id="ver" class="btn-sec">Ver mis pedidos</button>
          <button id="otro" class="btn-primario">Hacer otro pedido</button>
        </div>
      </div>`;
    contenedor.querySelector("#ver")?.addEventListener("click", () => navegar("#/mispedidos"));
    contenedor.querySelector("#otro")?.addEventListener("click", () => navegar("#/pedir"));
  }

  const CHIP_ESTADO: Record<string, string> = {
    PENDIENTE: "sin confirmar todavia",
    CONFIRMADO: "ya confirmado por la confitería",
  };

  /**
   * Un pedido vivo de este local para la misma fecha Y el mismo turno.
   *
   * Es facil mandar dos veces lo mismo (se cierra la app, se vuelve a entrar,
   * el borrador sigue cargado) y el segundo pedido entra a produccion igual.
   * Se avisa, pero NO se prohibe: a veces hay que agregar algo que falto.
   *
   * Si la consulta falla se devuelve null y el pedido sale sin preguntar: un
   * problema de senal en el shopping no puede trabar el pedido del dia.
   */
  async function pedidoDelMismoTurno(fecha: string): Promise<PedidoDeLocal | null> {
    try {
      const pedidos = await api.get<PedidoDeLocal[]>("/api/cliente/pedidos");
      return (
        pedidos.find(
          (p) =>
            p.cliente_id === clienteId &&
            p.fecha_entrega === fecha &&
            p.turno === turno &&
            p.estado !== "ANULADO",
        ) ?? null
      );
    } catch {
      return null;
    }
  }

  /** true = mandarlo igual. Solo se llama cuando ya hay uno del mismo turno. */
  function preguntarSiVaOtro(previo: PedidoDeLocal): Promise<boolean> {
    const turnoTexto = (TURNOS.find((t) => t.valor === turno)?.nombre ?? "").toLowerCase();
    dlg.innerHTML = `
      <div class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
        <h2 class="mb-2 text-lg font-bold">Ya hay un pedido para ese día</h2>
        <p class="text-sm text-masa-600">
          Para el <b>${esc(previo.fecha_entrega)}</b> de <b>${esc(turnoTexto)}</b> ya mandaste el
          pedido <b>#${esc(previo.id)}</b> de ${formatearPesos(previo.total)},
          ${esc(CHIP_ESTADO[previo.estado] ?? "")}.
        </p>
        <p class="mt-2 text-sm text-masa-600">
          Si querias <b>corregir</b> ese, mejor entra a Mis pedidos. Si esto es algo
          <b>además</b> de aquello, mandalo igual y van los dos.
        </p>
        <div class="mt-4 flex flex-wrap justify-end gap-2">
          <button id="dlg_ver" class="btn-sec">Ver el que ya hice</button>
          <button id="dlg_igual" class="btn-primario">Si, mandar igual</button>
        </div>
      </div>`;
    dlg.showModal();
    return new Promise<boolean>((resolver) => {
      // Ojo con el orden: resolver ANTES de close(), porque close() dispara el
      // listener de abajo en el acto y la primera resolucion es la que vale.
      dlg.querySelector("#dlg_ver")?.addEventListener("click", () => {
        resolver(false);
        dlg.close();
        navegar("#/mispedidos");
      });
      dlg.querySelector("#dlg_igual")?.addEventListener("click", () => {
        resolver(true);
        dlg.close();
      });
      // Escape o clic afuera: se cierra sin mandar nada.
      dlg.addEventListener("close", () => resolver(false), { once: true });
    });
  }

  contenedor.querySelector("#enviar")?.addEventListener("click", async () => {
    err.textContent = "";
    const lineas = [...cantidades.entries()]
      .filter(([, c]) => c > 0)
      .map(([producto_id, cantidad]) => ({ producto_id, cantidad }));
    if (lineas.length === 0) {
      err.textContent = "Cargá al menos un artículo.";
      return;
    }
    // Al corregir un pendiente la fecha no se toca: solo se revisa al crear.
    if (!editandoId) {
      if (!inpFecha.value) {
        err.textContent = "Elegí la fecha de entrega.";
        return;
      }
      if (esFechaPasada(inpFecha.value)) {
        err.textContent = "Esa fecha ya pasó: elegí hoy o un día siguiente.";
        inpFecha.focus();
        return;
      }
    }

    // El detalle que se muestra al final: se arma con lo que hay en pantalla,
    // asi la confirmacion no depende de otra vuelta al servidor.
    const detalle: LineaEnviada[] = lineas.map(({ producto_id, cantidad }) => {
      const it = disponibles.find((i) => i.producto.id === producto_id);
      return {
        descripcion: it?.producto.descripcion ?? "",
        cantidad,
        subtotal: calcularSubtotal(it?.precio ?? 0, cantidad),
      };
    });

    const boton = contenedor.querySelector("#enviar") as HTMLButtonElement;
    boton.disabled = true;

    // Solo al crear: corregir un pendiente nunca es un duplicado de si mismo.
    if (!editandoId) {
      const previo = await pedidoDelMismoTurno(inpFecha.value);
      if (previo && !(await preguntarSiVaOtro(previo))) {
        boton.disabled = false;
        return;
      }
    }

    try {
      const total = recalcular();
      if (editandoId) {
        await api.put(`/api/cliente/pedidos/${editandoId}`, { lineas });
      } else {
        await api.post("/api/cliente/pedidos", {
          cliente_id: clienteId,
          fecha_entrega: inpFecha.value,
          turno,
          lineas,
        });
      }
      localStorage.removeItem(borradorDe(clienteId));
      sessionStorage.removeItem(EDITANDO);
      pantallaEnviado(inpFecha.value, total, detalle);
    } catch (e) {
      boton.disabled = false;
      err.textContent = e instanceof Error ? e.message : "No se pudo enviar el pedido";
    }
  });

  pintar();
}
